import React, { useEffect, useState } from 'react';
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  PhoneOutgoing,
  Mic,
  MicOff,
  Loader2,
} from 'lucide-react';
import { CallState, TwilioCallContext } from '../../types/support';
import { cn, formatDuration, formatPhoneNumber } from '../../utils/helpers';

interface SupportCallControlsProps {
  phoneNumber?: string;
  callContext: TwilioCallContext;
  isReady: boolean;
  isConfigured: boolean;
  onMakeCall: () => void;
  onHangup: () => void;
  onToggleMute: () => void;
}

const SupportCallControls: React.FC<SupportCallControlsProps> = ({
  phoneNumber,
  callContext,
  isReady,
  isConfigured,
  onMakeCall,
  onHangup,
  onToggleMute,
}) => {
  const { state, isMuted, duration } = callContext;
  const isIdle = state === 'idle';
  const isInCall = state === 'in-call';
  const isConnecting = state === 'connecting' || state === 'ringing';

  // Don't show if voice is not configured
  if (!isConfigured) {
    return (
      <div className="p-4 bg-card border border-border rounded-lg">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Phone className="h-5 w-5" />
          <div>
            <p className="text-sm font-medium">Voice Calling Not Configured</p>
            <p className="text-xs">Contact admin to enable WebRTC calling</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-card border border-border rounded-lg space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-2 h-2 rounded-full',
              isReady ? 'bg-success' : 'bg-warning animate-pulse'
            )}
          />
          <span className="text-xs text-muted-foreground">
            {isReady ? 'Ready' : 'Connecting...'}
          </span>
        </div>
        {phoneNumber && (
          <span className="text-sm font-mono text-foreground">
            {formatPhoneNumber(phoneNumber)}
          </span>
        )}
      </div>

      {/* Call state display */}
      {!isIdle && (
        <div className="flex items-center justify-center py-3">
          <div className="text-center">
            {isConnecting && (
              <div className="flex items-center gap-2 text-primary">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm font-medium">
                  {state === 'ringing' ? 'Ringing...' : 'Connecting...'}
                </span>
              </div>
            )}
            {isInCall && (
              <div className="flex flex-col items-center gap-1">
                <span className="text-2xl font-mono text-success">
                  {formatDuration(duration)}
                </span>
                <span className="text-xs text-success">Connected</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-3">
        {isIdle ? (
          <button
            onClick={onMakeCall}
            disabled={!isReady || !phoneNumber}
            className={cn(
              'flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all',
              isReady && phoneNumber
                ? 'bg-success text-white hover:bg-success/90 shadow-lg shadow-success/20'
                : 'bg-secondary-700/50 text-muted-foreground cursor-not-allowed'
            )}
          >
            <PhoneOutgoing className="h-5 w-5" />
            Call Customer
          </button>
        ) : (
          <>
            {/* Mute button - only during call */}
            {isInCall && (
              <button
                onClick={onToggleMute}
                className={cn(
                  'p-3 rounded-full transition-colors',
                  isMuted
                    ? 'bg-destructive/20 text-destructive'
                    : 'bg-secondary-700/50 text-foreground hover:bg-secondary-700'
                )}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <MicOff className="h-5 w-5" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </button>
            )}

            {/* Hangup button */}
            <button
              onClick={onHangup}
              className="flex items-center gap-2 px-6 py-3 rounded-full font-medium bg-destructive text-white hover:bg-destructive/90 transition-colors"
            >
              <PhoneOff className="h-5 w-5" />
              {isInCall ? 'End Call' : 'Cancel'}
            </button>
          </>
        )}
      </div>

      {/* No phone number warning */}
      {!phoneNumber && isIdle && (
        <p className="text-xs text-center text-muted-foreground">
          Customer has no phone number on file
        </p>
      )}
    </div>
  );
};

export default SupportCallControls;
